import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ScrollText, X, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import '../styles/AdminQaToast.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const POLL_MS = 30000;
const SEEN_KEY = 'cadence-admin-qa-seen-ids';

const loadSeen = () => {
  try {
    const raw = sessionStorage.getItem(SEEN_KEY);
    return raw ? new Set(JSON.parse(raw)) : new Set();
  } catch {
    return new Set();
  }
};

/**
 * Lightweight admin-side toast: polls /api/admin/clinical-report-threads/unread
 * every 30s and slides in a luxe eggplant/gold toast when a NEW patient
 * question lands during this session. "New" is anything we haven't seen on
 * this device before (tracked in sessionStorage to survive route changes but
 * not full restarts).
 */
const AdminQaToast = ({ token }) => {
  const navigate = useNavigate();
  const [toast, setToast] = useState(null);
  const seenRef = useRef(loadSeen());
  const intervalRef = useRef(null);

  const persistSeen = useCallback(() => {
    try {
      sessionStorage.setItem(SEEN_KEY, JSON.stringify(Array.from(seenRef.current)));
    } catch {
      /* ignore quota errors */
    }
  }, []);

  const poll = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API_URL}/api/admin/clinical-report-threads/unread`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return;
      const data = await res.json();
      const cases = data.cases || [];

      // Update PWA app badge (dock/home-screen icon) so the count is visible
      // even when the tab is backgrounded. Silently no-ops on browsers that
      // don't support the Badging API (older Firefox, Safari <16.4 desktop).
      try {
        if (typeof navigator !== 'undefined' && 'setAppBadge' in navigator) {
          if (data.total_unread > 0) {
            await navigator.setAppBadge(data.total_unread);
          } else {
            await navigator.clearAppBadge?.();
          }
        }
      } catch {
        /* ignore — badging is best-effort */
      }

      // Find the newest case whose latest_at signature we haven't surfaced yet.
      for (const c of cases) {
        const sig = `${c.case_id}:${c.latest_at}`;
        if (!seenRef.current.has(sig)) {
          seenRef.current.add(sig);
          persistSeen();
          setToast({
            case_id: c.case_id,
            case_number: c.case_number,
            author: c.latest_author_name,
            preview: c.latest_preview,
            count: c.count,
          });
          break; // Surface one at a time; rest queue on next poll
        }
      }
    } catch {
      /* network blip — silently retry next interval */
    }
  }, [token, persistSeen]);

  useEffect(() => {
    if (!token) return;
    // Prime the seen set with whatever's already there on first mount so we
    // don't surface old questions as "new". Also paint the initial app badge.
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/admin/clinical-report-threads/unread`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          (data.cases || []).forEach((c) => {
            seenRef.current.add(`${c.case_id}:${c.latest_at}`);
          });
          persistSeen();
          try {
            if (typeof navigator !== 'undefined' && 'setAppBadge' in navigator) {
              if (data.total_unread > 0) {
                await navigator.setAppBadge(data.total_unread);
              } else {
                await navigator.clearAppBadge?.();
              }
            }
          } catch {
            /* ignore */
          }
        }
      } catch {
        /* ignore */
      }
      intervalRef.current = setInterval(poll, POLL_MS);
    })();
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      // Don't clear the badge on unmount — count should persist across route
      // changes; only the next poll cycle (or a sign-out) updates it.
    };
  }, [token, poll, persistSeen]);

  if (!toast) return null;

  return (
    <div className="qa-toast" role="alert" data-testid="admin-qa-toast">
      <div className="qa-toast-icon">
        <ScrollText size={20} />
      </div>
      <div className="qa-toast-body">
        <div className="qa-toast-headline">
          New patient question
          <span className="qa-toast-case">{toast.case_number || toast.case_id.slice(0, 8)}</span>
          {toast.count > 1 && <span className="qa-toast-count">+{toast.count - 1} more</span>}
        </div>
        <div className="qa-toast-author">{toast.author || 'Patient'}</div>
        <p className="qa-toast-preview">{toast.preview}</p>
      </div>
      <div className="qa-toast-actions">
        <button
          type="button"
          className="qa-toast-open"
          onClick={() => {
            navigate(`/admin/intakes/${toast.case_id}`);
            setToast(null);
          }}
          data-testid="admin-qa-toast-open"
        >
          Open <ChevronRight size={14} />
        </button>
        <button
          type="button"
          className="qa-toast-close"
          onClick={() => setToast(null)}
          aria-label="Dismiss"
          data-testid="admin-qa-toast-close"
        >
          <X size={16} />
        </button>
      </div>
    </div>
  );
};

export default AdminQaToast;
